// Nivel de servicio y cobertura POR CATEGORÍA (ticket 11).
//
// Los dos parámetros ya existían globales. Poder apartarse del global por categoría tiene un
// efecto directo en plata: subir el nivel de servicio sube el stock de seguridad, y eso es
// capital inmovilizado. Que se pueda subir solo donde importa es la diferencia entre cuidar el
// servicio y comprar de más en todo el catálogo.
//
// Cada fila muestra el valor efectivo: si no tiene override, muestra el global en gris y dice
// que lo sigue. Cambiarlo crea el override; el botón de la derecha lo borra y vuelve al global.
import { useState } from 'react';
import { Loader2, RotateCcw } from 'lucide-react';
import { Button, EditableCell, Table, Thead, Tr, Td } from '@/components/ui';
import { useRecalcularForecast, useUpdateCategorySetting } from '@/lib/hooks';
import { formatNumber } from '@shared/format';
import { cn } from '@/lib/cn';
import type { CategorySetting, Settings } from '@shared/types';

const COLUMNAS = [
  { key: 'cat', label: 'Categoría' },
  { key: 'prods', label: 'Productos', align: 'right' as const },
  { key: 'nivel', label: 'Nivel de Servicio', align: 'right' as const, wrap: true },
  { key: 'cob', label: 'Días de Cobertura', align: 'right' as const, wrap: true },
  { key: 'acc', label: '' },
];

export function CategoryParams({ settings }: { settings: Settings }) {
  const guardar = useUpdateCategorySetting();
  const recalcular = useRecalcularForecast();
  const [aviso, setAviso] = useState<string | null>(null);
  const [pendiente, setPendiente] = useState(false);

  const aplicar = async (cat: CategorySetting, cambio: Partial<Pick<CategorySetting, 'serviceLevel' | 'coverageTargetDays'>>) => {
    try {
      await guardar.mutateAsync({
        category: cat.category,
        serviceLevel: cambio.serviceLevel !== undefined ? cambio.serviceLevel : cat.serviceLevel,
        coverageTargetDays:
          cambio.coverageTargetDays !== undefined ? cambio.coverageTargetDays : cat.coverageTargetDays,
      });
      setPendiente(true);
      setAviso(`${cat.category} guardada`);
    } catch (e) {
      setAviso((e as Error).message);
    }
  };

  const conOverride = settings.categories.filter((c) => c.serviceLevel != null || c.coverageTargetDays != null);

  return (
    <div className="space-y-3">
      <p className="text-2xs leading-relaxed text-ink-faint">
        Lo que no configures acá usa los valores globales de arriba
        (<span className="text-ink">{settings.serviceLevel}%</span> y{' '}
        <span className="text-ink">{settings.coverageTargetDays} días</span>). Subir el
        nivel de servicio de una categoría sube su stock de seguridad: cubre más quiebres, pero
        inmoviliza más capital.
      </p>

      {/* El cambio no se ve hasta recalcular: los parámetros entran en el stock de seguridad y en
          el punto de reorden de cada producto de esa categoría. */}
      {pendiente && (
        <div className="surface flex flex-wrap items-center justify-between gap-3 border-l-2 border-l-primary p-3">
          <p className="text-xs text-ink-muted">
            Cambiaste parámetros. El stock de seguridad y el punto de reorden se calculan con esto.
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              disabled={recalcular.isPending}
              onClick={() =>
                recalcular
                  .mutateAsync()
                  .then(() => {
                    setPendiente(false);
                    setAviso('Forecast recalculado con los parámetros nuevos.');
                  })
                  .catch((e) => setAviso((e as Error).message))
              }
              icon={recalcular.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : undefined}
            >
              {recalcular.isPending ? 'Recalculando…' : 'Recalcular forecast'}
            </Button>
            <Button variant="ghost" onClick={() => setPendiente(false)} disabled={recalcular.isPending}>
              Después
            </Button>
          </div>
        </div>
      )}

      {aviso && <p className="text-2xs text-ink-muted">{aviso}</p>}

      {settings.categories.length === 0 ? (
        <p className="py-6 text-center text-sm text-ink-faint">
          Todavía no hay categorías: se toman del catálogo al actualizar los datos.
        </p>
      ) : (
        <>
          <Table>
            <Thead columns={COLUMNAS} />
            <tbody>
              {settings.categories.map((c) => (
                <Fila key={c.category} cat={c} settings={settings} onAplicar={aplicar} />
              ))}
            </tbody>
          </Table>
          <p className="text-2xs text-ink-faint">
            {conOverride.length === 0
              ? 'Ninguna categoría se aparta del global todavía.'
              : `${conOverride.length} categoría${conOverride.length === 1 ? '' : 's'} con valores propios.`}
          </p>
        </>
      )}
    </div>
  );
}

function Fila({
  cat,
  settings,
  onAplicar,
}: {
  cat: CategorySetting;
  settings: Settings;
  onAplicar: (cat: CategorySetting, cambio: Partial<CategorySetting>) => void;
}) {
  const propio = cat.serviceLevel != null || cat.coverageTargetDays != null;
  return (
    <Tr className={cn(propio && 'border-l-2 border-l-primary')}>
      <Td>
        <p className="text-sm text-ink-strong">{cat.category}</p>
      </Td>
      <Td align="right">
        <span className="text-2xs text-ink-faint">{formatNumber(cat.productos)}</span>
      </Td>
      <Td align="right">
        <Valor
          propio={cat.serviceLevel}
          global={settings.serviceLevel}
          sufijo="%"
          onGuardar={(v) => onAplicar(cat, { serviceLevel: v })}
        />
      </Td>
      <Td align="right">
        <Valor
          propio={cat.coverageTargetDays}
          global={settings.coverageTargetDays}
          sufijo="d"
          onGuardar={(v) => onAplicar(cat, { coverageTargetDays: v })}
        />
      </Td>
      <Td>
        {propio && (
          <button
            onClick={() => onAplicar(cat, { serviceLevel: null, coverageTargetDays: null })}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-2xs text-ink-faint hover:bg-hover hover:text-ink-strong"
            title="Volver a los valores globales"
          >
            <RotateCcw className="h-3 w-3" />
            global
          </button>
        )}
      </Td>
    </Tr>
  );
}

/** Muestra el valor propio, o el global en gris cuando la categoría no tiene override. */
function Valor({
  propio,
  global,
  sufijo,
  onGuardar,
}: {
  propio: number | null;
  global: number;
  sufijo: string;
  onGuardar: (v: number) => void;
}) {
  if (propio == null) {
    return (
      <EditableCell
        valor={global}
        formato={(v) => `${v}${sufijo}`}
        onGuardar={onGuardar}
        className="opacity-50"
      />
    );
  }
  return <EditableCell valor={propio} formato={(v) => `${v}${sufijo}`} onGuardar={onGuardar} />;
}
